import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import { Badge } from "@/components/ui/badge";
import { Plus, FilterX, Calendar, Search, MoreHorizontal } from "lucide-react";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { ProgramTableSkeleton } from "@/components/ui/program-table-skeleton";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetClose,
} from "@/components/ui/sheet";
import { ProgramApi } from "@/api";
import { getFormattedDate, getFormattedDateTime } from "@/utils";
import { EnhancedPaginationControl } from "@/components/common/EnhancedPaginationControl";
import { PAGE_SIZE } from "@/constants/common.constant";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { TransferApi, TransferPartnerListPayload } from "@/api/transfer.api";
import { LoaderButton } from "@/components/ui/loader-button";
import { PermissionGuard } from "@/components/common/PermissionGuard";
import { PERMISSION_CODES } from "@/constants/permissions";
import { BASE_URL } from "@/config";

const filters = {
  pageSize: PAGE_SIZE,
  programCode: null,
  programName: null,
  programType: "EARN",
  sortBy: "createdDate",
  sortOrder: "desc",
};

const DELAY = 500;

interface TransferPartnerFilters extends TransferPartnerListPayload {
  transferPartnerName?: string | null;
  country?: string | null;
}

const defaultTransferPartnerFilters: TransferPartnerFilters = {
  pageNumber: 0,
  pageSize: PAGE_SIZE,
  sortBy: "createdDate",
  sortOrder: "desc",
  transferPartnerName: null,
  country: null,
};

const TransferPartners = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredPartners, setFilteredPartners] = useState<any[]>([]);
  const [totalPartners, setTotalPartners] = useState(0);
  const [page, setPage] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const totalPages = Math.ceil(totalPartners / rowsPerPage);
  const debounceTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [partnerToDelete, setPartnerToDelete] = useState<any | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  function debounce<T extends (...args: any[]) => void>(
    func: T,
    delay: number
  ) {
    return function (...args: Parameters<T>) {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
      debounceTimeout.current = setTimeout(() => {
        func(...args);
        debounceTimeout.current = null;
      }, delay);
    };
  }

  async function fetchPartners(
    filters: TransferPartnerFilters = defaultTransferPartnerFilters
  ) {
    try {
      setIsLoading(true);
      const { data } = await TransferApi.getTransferPartnersFromDB({
        ...filters,
        pageNumber: filters.pageNumber,
        pageSize: filters.pageSize,
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder,
        transferPartnerName: filters.transferPartnerName,
        country: filters.country,
      });
      setTotalPartners(data?.totalNumberOfTransferPartners || 0);

      const partners = data?.transferPartnerVOList?.map((partner) => ({
        ...partner,
        id: partner?.id,
        name: partner?.partnerName,
        partnerCode: partner?.partnerCode,
        city: partner?.city || "-",
        country: partner?.country || "-",
        currency: partner?.currencyVO?.iso3CurrencyCode || "-",
        costPerPoint: partner?.costPerPoint,
        partnerLogoUri: partner?.partnerLogoUri,
        createdDateInMillis: partner?.createdDateInMillis,
        explorerUrl: partner?.explorerUrl || null,
      }));
      setFilteredPartners(partners || []);
    } catch (err) {
      toast.error("Failed to fetch transfer partners");
    } finally {
      setIsLoading(false);
    }
  }
  useEffect(() => {
    fetchPartners({
      ...defaultTransferPartnerFilters,
      pageNumber: page,
      pageSize: rowsPerPage,
    } as TransferPartnerFilters);
  }, [page, rowsPerPage]);

  const resetFilters = () => {
    setSearchQuery("");
    setPage(0);
    fetchPartners({
      ...defaultTransferPartnerFilters,
      pageNumber: 0,
      pageSize: rowsPerPage,
    } as TransferPartnerFilters);
  };

  const debouncedSearch = debounce((query) => {
    setPage(0);
    fetchPartners({
      ...defaultTransferPartnerFilters,
      pageNumber: 0,
      pageSize: rowsPerPage,
      transferPartnerName: query,
    } as TransferPartnerFilters);
  }, DELAY);

  const confirmDeletePartner = async () => {
    if (!partnerToDelete?.id) return;
    try {
      setIsDeleting(true);
      await TransferApi.deletePartner(partnerToDelete.id);
      toast.success(
        `Program ${partnerToDelete.name || ""} deleted successfully`,
      );
      setDeleteDialogOpen(false);
      setPartnerToDelete(null);
      fetchPartners({
        ...defaultTransferPartnerFilters,
        pageNumber: page,
        pageSize: rowsPerPage,
        transferPartnerName: searchQuery || null,
      } as TransferPartnerFilters);
    } catch (error: any) {
      toast.error(
        error?.response?.data?.errorMessage ||
          error?.message ||
          "Failed to delete program",
      );
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Program</h1>
          <p className="text-gray-500 mt-1">
            Manage your Program and their attributes
          </p>
        </div>
        <div className="flex gap-3">
          <PermissionGuard
            permissionCode={PERMISSION_CODES.CREATE_TRANSFER_PARTNER}
          >
            <Button className="bg-blue-600 hover:bg-blue-700" asChild>
              <Link to="/xpandpoint/transfer-programs/create">
                {/* <Link to="/xpandpoint/partner/create"> */}
                <Plus className="h-4 w-4 mr-2" /> Add Program
              </Link>
            </Button>
          </PermissionGuard>
        </div>
      </div>

      {/* Filter section (temporarily hidden) */}
      {true && (
        <div className="flex flex-col sm:flex-row gap-4 items-end sm:items-center bg-white p-4 rounded-lg border border-gray-100 shadow-sm">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
              <Input
                id="search"
                placeholder="Search by program name"
                className="pl-9"
                value={searchQuery}
                onChange={(e) => {
                  const query = e.target.value;
                  setSearchQuery(query);
                  setPage(0);
                  debouncedSearch(query);
                }}
              />
            </div>
          </div>

          <Button
            variant="outline"
            size="icon"
            onClick={resetFilters}
            className="h-10 w-10"
            disabled={isLoading}
          >
            <FilterX className="h-4 w-4" />
          </Button>
        </div>
      )}

      <DataTable
        title="Program"
        data={isLoading ? [] : filteredPartners}
        columns={[
          {
            header: "Created Date",
            accessorKey: "createdDate",
            cell: (row) => (
              <div className="text-sm text-gray-600 min-w-[140px] w-[140px]">
                {row.createdDateInMillis
                  ? getFormattedDateTime(row.createdDateInMillis)
                  : "-"}
              </div>
            ),
          },
          {
            header: "Logo",
            id: "logo",
            cell: (row) => (
              <div className="w-10 h-10 rounded-md bg-gray-100 flex items-center justify-center">
                <img
                  src={
                    row.partnerLogoUri
                      ? `${BASE_URL}${row.partnerLogoUri}`
                      : "/placeholder.svg"
                  }
                  alt={row.name || "Partner Logo"}
                  className="max-h-8 max-w-8 object-contain"
                  onError={(e) => {
                    e.currentTarget.src = "/placeholder.svg";
                  }}
                />
              </div>
            ),
          },
          {
            header: "Name",
            accessorKey: "name",
          },
          {
            header: "Code",
            accessorKey: "partnerCode",
          },
          {
            header: "City/Country",
            accessorKey: "cityCountry",
            cell: (row) => (
              <div>
                <div>{row.city}</div>
                <div className="text-xs text-gray-500 mt-0.5">
                  {row.country}
                </div>
              </div>
            ),
          },
          {
            header: "Currency",
            accessorKey: "currency",
          },
          {
            header: "Redemption CPP",
            accessorKey: "redemptionCpp",
            cell: (row) => (
              <div className="text-sm text-gray-600">
                {row.costPerPoint ? `${row.costPerPoint}` : "-"}
              </div>
            ),
          },

          {
            header: "Actions",
            id: "actions",
            cell: (row) => (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                    <span className="sr-only">Open menu</span>
                    <MoreHorizontal size={16} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  className="bg-white min-w-[8rem] p-1"
                >
                  <DropdownMenuItem asChild>
                    <Link
                      to={`/xpandpoint/transfer-programs/${row.id}`}
                      // to={`/xpandpoint/transfer-partners/${row.id}`}
                      state={{ transferPartner: row }}
                    >
                      View Details
                    </Link>
                  </DropdownMenuItem>
                  {row.explorerUrl ? (
                    <DropdownMenuItem
                      onClick={() =>
                        window.open(
                          row.explorerUrl,
                          "_blank",
                          "noopener,noreferrer",
                        )
                      }
                    >
                      View On Blockchain
                    </DropdownMenuItem>
                  ) : null}
                  <DropdownMenuItem
                    onClick={() => {
                      setPartnerToDelete(row);
                      setDeleteDialogOpen(true);
                    }}
                    className="text-red-600"
                  >
                    Delete Program
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ),
          },
        ]}
        customTableBody={isLoading ? <ProgramTableSkeleton /> : undefined}
      />
      {!isLoading && totalPages ? (
        <div className="flex items-center justify-between mt-4">
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-500">Rows per page:</span>
            <Select
              value={rowsPerPage.toString()}
              onValueChange={(value) => {
                setRowsPerPage(Number(value));
                setPage(0);
              }}
            >
              <SelectTrigger className="w-[80px]">
                <SelectValue placeholder={rowsPerPage.toString()} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="5">5</SelectItem>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="20">20</SelectItem>
                <SelectItem value="50">50</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <EnhancedPaginationControl
            totalPages={totalPages}
            currentPage={page}
            onPageChange={(page) => setPage(page)}
          />
          <div className="w-[160px]" /> {/* Spacer to balance layout */}
        </div>
      ) : null}
      {/* Delete Program Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Program</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the program "
              {partnerToDelete?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <LoaderButton
              loading={isDeleting}
              variant="destructive"
              className="bg-red-600 hover:bg-red-700"
              onClick={confirmDeletePartner}
            >
              Delete
            </LoaderButton>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default TransferPartners;
